/**
 * ChatWidget Component
 * Main chat widget container - FAB + Panel
 * Renders end-user chat interface with real-time messaging
 * Consistent with Phase 3 admin dashboard patterns
 * Theme-aware with Tailwind dark: prefix classes
 */

import { useState } from 'react';
import { ChatWidgetProps } from '../../types/liveChat';
import { useChat } from '../../hooks/useChat';
import { ChatFAB } from './ChatFAB';
import { ChatPanel } from './ChatPanel';

export const ChatWidget: React.FC<ChatWidgetProps & { isControlled?: boolean; controlledIsOpen?: boolean }> = ({
  initialVisible = false,
  onClose,
  onOpen,
  socketUrl,
  isControlled = false,
  controlledIsOpen
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(initialVisible);
  const isOpen = isControlled ? controlledIsOpen ?? false : internalIsOpen;
  
  const setIsOpen = (value: boolean | ((prev: boolean) => boolean)) => {
    if (!isControlled) {
      setInternalIsOpen(typeof value === 'function' ? value(internalIsOpen) : value);
    }
  };
  const [inputValue, setInputValue] = useState('');

  // Initialize chat
  const {
    conversation,
    messages,
    socket,
    isConnected,
    isLoading,
    isTyping,
    error,
    unreadCount,
    startConversation,
    sendMessage,
    setIsTyping
  } = useChat(isOpen);

  /**
   * Handle FAB click
   */
  const handleFABClick = () => {
    if (!isOpen) {
      if (!isControlled) setIsOpen(true);
      onOpen?.();

      // Start conversation if none exists
      if (!conversation) {
        startConversation();
      }
    } else {
      if (!isControlled) setIsOpen(false);
      onClose?.();
    }
  };

  /**
   * Handle close button
   */
  const handlePanelClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  /**
   * Handle send message
   */
  const handleSendMessage = async (content: string, imageAttachment?: any) => {
    if (!content.trim() && !imageAttachment) return;

    try {
      // Start conversation if needed - pass initial message to include it in conversation
      const isNewConversation = !conversation;
      if (isNewConversation) {
        await startConversation('General Inquiry', content);
      }

      // Emit typing started
      setIsTyping(true);

      // Send message separately only if conversation already existed
      // (if new conversation, message was sent via startConversation)
      if (!isNewConversation) {
        await sendMessage(content, imageAttachment);
      }

      // Clear input
      setInputValue('');

      // Emit typing stopped
      setTimeout(() => setIsTyping(false), 500);
    } catch (err: any) {
      console.error('Error sending message:', err);
    }
  };

  /**
   * Handle input change and typing indicator
   */
  const handleInputChange = (value: string) => {
    setInputValue(value);

    // Send typing indicator if has socket connection
    if (socket?.connected && value.trim()) {
      setIsTyping(true);
    }
  };

  return (
    <>
      {/* FAB Button */}
      <ChatFAB
        isOpen={isOpen}
        onClick={handleFABClick}
      />

      {/* Chat Panel */}
      {isOpen && (
        <ChatPanel
          conversation={conversation}
          messages={messages}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onSend={handleSendMessage}
          onClose={handlePanelClose}
          isTyping={isTyping}
          isLoading={isLoading}
        />
      )}

      {/* Error indicator */}
      {error && isOpen && (
        <div className="fixed bottom-20 right-4 sm:bottom-24 sm:right-72 text-xs text-error-500 bg-error-50 dark:bg-error-900/20 px-3 py-2 rounded z-40">
          {error}
        </div>
      )}
    </>
  );
};

export default ChatWidget;